import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';

import ActionButton from 'components/buttons/action-button/ActionButton';

import SOVGraphOverTime from 'modules/insights/sov/components/graphs/SOVGraphOverTime';
import SOVAnalysisPosts from 'modules/insights/sov/components/analysis/posts/SOVAnalysisPosts';
import SOVTopInfluencers from 'modules/insights/sov/components/analysis/top-creators/SOVTopInfluencers';
import SOVAudience from 'modules/insights/sov/components/audience/collabary/instagram/SOVAudience';
import SOVAnalysisResultsSection from './section/SOVAnalysisResultsSection';
import SOVAnalysisSummary from 'modules/insights/sov/components/analysis/summary/SOVAnalysisSummary';

import { trackEventSOVReport } from 'modules/tracking/utils/tracking';
import { fetchPosts, postsUpdateFiltersScreenState, postsUpdateScreenState } from 'modules/insights/sov/reducers/posts/collabary/sov-posts';
import { runAnalysis } from 'modules/insights/sov/reducers/analysis/collabary/sov-analysis';
import { runAnalysis as runAnalysisConsumerInsights } from 'modules/insights/sov/reducers/analysis/consumer-insights/sov-analysis';
import { fetchTopInfluencers } from 'modules/insights/sov/reducers/top-creators/collabary/sov-top-influencers';

import { __t, applyTranslations } from 'utils/language';
import { getGraphDateInterval } from 'utils/graphs/utils';
import { scrollToMissingField } from 'utils/page';

import './sov-analysis-results.scss';

import type {
  LineGraphEChartsObject,
  LocationWindow,
  SOVAnalysis as SOVAnalysisResult,
  SOVReportType
} from 'types';
import type { SOVQueryLocal, SOVReport } from 'modules/insights/sov/types';

applyTranslations(require('modules/insights/sov/shared/sov-shared.i18n.json'));
applyTranslations(require('./sov-analysis-results.i18n.json'));

type OwnProps = {
  reportType: SOVReportType,
  isConsumerInsights?: boolean,
  isFreemium?: boolean,
  isValidSetup: boolean,
  location: LocationWindow,
  chartInstanceCallback?: (a: LineGraphEChartsObject) => void,
  onFormStatusCheck: () => void
};

type StateProps = {
  report: SOVReport,
  analysis: SOVAnalysisResult,
  queriesMerged: Array<SOVQueryLocal>,
  analysisHasBeenRun: boolean,
  isLoadingAnalysis: boolean
};

type DispatchProps = {
  dispatchRunAnalysis: (report: SOVReport, isConsumerInsights: boolean | undefined) => void,
  dispatchFetchTopInfluencers: (reportType: SOVReportType) => void,
  dispatchFetchPosts: (report: SOVReport, reportType: SOVReportType) => void,
  dispatchPostsUpdateScreenState: (b: any, a: string) => void,
  dispatchUpdateFiltersScreenState: (b: any, a: string) => void
};

type Props = OwnProps & StateProps & DispatchProps;

const SHARED_MSG_KEY = 'sov_shared';
const MSG_KEY = 'sov_analysis_results';

class SOVAnalysisResults extends React.PureComponent<Props> {
  static defaultProps = {
    disabled: false,
    isFreemium: false
  };

  containerRef: React.RefObject<HTMLDivElement> = React.createRef();

  getResultsOverTime() {
    const { analysis, isLoadingAnalysis, report, reportType, chartInstanceCallback } = this.props;
    const reportName = (report && report.name) || __t(`${MSG_KEY}.labels.unnamed_report`);
    const dateInterval = getGraphDateInterval(report.startDate, report.endDate);
    const graphKey = (analysis.graphOverTime || []).map(item => item.searchTerm).join(',');

    return (
      <SOVAnalysisResultsSection
        isEmpty={isEmpty(analysis) || isEmpty(analysis.graphOverTime)}
        isLoading={isLoadingAnalysis}
        header={__t(`${MSG_KEY}.labels.report`)}
        subline={__t(`${SHARED_MSG_KEY}.messages.included_results`)}
      >
        <SOVGraphOverTime
          key={`sov-analysis-results__graph--${graphKey}`}
          chartInstanceCallback={chartInstanceCallback}
          results={analysis.graphOverTime}
          reportName={reportName}
          reportType={reportType}
          dateInterval={dateInterval}
          totalUniqueInfluencers={analysis.totalUniqueContentCreators}
        />
      </SOVAnalysisResultsSection>
    );
  }

  getAnalysisSummary() {
    const { analysis, reportType, isLoadingAnalysis } = this.props;
    const summaryTable = analysis.summaryTable || {};
    return (
      <SOVAnalysisResultsSection
        isEmpty={isEmpty(analysis) || isEmpty(analysis.summaryTable)}
        isLoading={isLoadingAnalysis}
        header={__t(`${MSG_KEY}.labels.analysis_summary`)}
      >
        <SOVAnalysisSummary
          summaryTable={summaryTable}
          reportType={reportType}
        />
      </SOVAnalysisResultsSection>
    );
  }

  getAudienceData() {
    const { analysis, isLoadingAnalysis, report } = this.props;

    return (
      <SOVAnalysisResultsSection
        isEmpty={isEmpty(analysis)}
        isLoading={isLoadingAnalysis}
        withStyles={false}
        header={__t(`${MSG_KEY}.labels.audience_statistics`)}
      >
        <SOVAudience
          report={report}
        />
      </SOVAnalysisResultsSection>
    );
  }

  handleFetchPosts = (): void => {
    const { report, reportType, dispatchFetchPosts } = this.props;

    dispatchFetchPosts(report, reportType);
  };

  handleAnalysis = (): void => {
    const {
      report,
      reportType,
      isValidSetup,
      onFormStatusCheck,
      isConsumerInsights,
      dispatchRunAnalysis,
      dispatchFetchPosts,
      dispatchFetchTopInfluencers,
      dispatchPostsUpdateScreenState,
      dispatchUpdateFiltersScreenState
    } = this.props;

    onFormStatusCheck();
    if (!isValidSetup) {
      scrollToMissingField(true);
      return;
    }

    const containerRef = this.containerRef && this.containerRef.current;

    if (containerRef) {
      containerRef.scrollIntoView({ behavior: 'smooth' });
    }

    dispatchRunAnalysis(report, isConsumerInsights);

    if (!isConsumerInsights) {
      dispatchUpdateFiltersScreenState([], 'queryIds');
      dispatchPostsUpdateScreenState('', 'channel');
      dispatchPostsUpdateScreenState(1, 'page');
      dispatchFetchPosts(report, reportType);
      dispatchFetchTopInfluencers(reportType);
    }
  };

  renderRunAnalysisButton(): React.ReactNode {
    return (
      <div className="sov-analysis-results__button">
        <ActionButton
          color="primary"
          width="maximum"
          onClick={this.handleAnalysis}
        >
          {__t(`${MSG_KEY}.buttons.run_analysis`)}
        </ActionButton>
      </div>
    );
  }

  renderRemainingItems(): React.ReactNode {
    const {
      reportType,
      queriesMerged,
      location,
      isLoadingAnalysis,
      isFreemium
    } = this.props;

    return (
      <React.Fragment>
        <SOVTopInfluencers
          reportType={reportType}
          isFreemium={isFreemium ?? false}
        />
        {
          reportType === 'instagram' &&
            this.getAudienceData()
        }
        <SOVAnalysisPosts
          key={String(isLoadingAnalysis)}
          queriesMerged={queriesMerged}
          reportType={reportType}
          location={location}
          isFreemium={isFreemium ?? false}
          onFetchPosts={this.handleFetchPosts}
        />
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { analysisHasBeenRun, isConsumerInsights } = this.props;

    return (
      <section className="sov-analysis-results">
        {this.renderRunAnalysisButton()}
        <div
          ref={this.containerRef}
          className="sov-analysis-results__container"
        >
          {
            analysisHasBeenRun && (
              <React.Fragment>
                {this.getResultsOverTime()}
                {this.getAnalysisSummary()}
                {
                  !isConsumerInsights &&
                    this.renderRemainingItems()
                }
              </React.Fragment>
            )
          }
        </div>
      </section>
    );
  }
}

const mapStateToProps = ({
  sovAnalysis: {
    analysis,
    isLoading,
    analysisHasBeenRun
  },
  sovAnalysisConsumerInsights: {
    analysis: analysisConsumerInsights,
    analysisHasBeenRun: analysisHasBeenRunConsumerInsights,
    isLoading: isLoadingConsumerInsights
  },
  sovReports: {
    report
  },
  sovReportsConsumerInsights: {
    report: reportConsumerInsights
  },
  sovQueries: {
    queriesMerged
  }
}, ownProps): StateProps => ({
  analysis: ownProps.isConsumerInsights ? analysisConsumerInsights : analysis,
  isLoadingAnalysis: ownProps.isConsumerInsights ? isLoadingConsumerInsights : isLoading,
  analysisHasBeenRun: ownProps.isConsumerInsights ? analysisHasBeenRunConsumerInsights : analysisHasBeenRun,
  report: ownProps.isConsumerInsights ? reportConsumerInsights : report,
  queriesMerged: ownProps.isConsumerInsights ? [] : queriesMerged
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchRunAnalysis: (report: SOVReport, isConsumerInsights: boolean | undefined) => {
    if (isConsumerInsights) {
      dispatch(runAnalysisConsumerInsights(report));
    } else {
      dispatch(runAnalysis(report));
    }

    trackEventSOVReport('analysis', report);
  },
  dispatchFetchPosts(report: SOVReport, reportType: SOVReportType) {
    trackEventSOVReport('posts', report);
    dispatch(fetchPosts(report, reportType));
  },
  dispatchFetchTopInfluencers: (reportType: SOVReportType) => (
    dispatch(fetchTopInfluencers(false, reportType))
  ),
  dispatchPostsUpdateScreenState: (value: any, propName: string) => (
    dispatch(postsUpdateScreenState(value, propName))
  ),
  dispatchUpdateFiltersScreenState: (value: any, propName: string) => (
    dispatch(postsUpdateFiltersScreenState(value, propName))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(SOVAnalysisResults);
